import classNames from "classnames";
import { State } from "redux/store";
import { connect, useDispatch } from "react-redux";
import AutoSizer from "react-virtualized-auto-sizer";
import { VariableSizeList as List } from "react-window";
import { CSSProperties, RefObject, useState } from "react";

import {
  CVATProject,
  CVATTask,
  CVATTypes,
  RawCVATProject,
  RawCVATTask,
} from "types";
import { kFormatter } from "helpers/utility/formatters";
import {
  getFormattedCVATData,
  getNewDatasetId,
  getRawCVATData,
} from "redux/CreateDataset/selectors";

import ChevronIcon from "assets/icons/ChevronIcon";
import SearchIcon from "assets/icons/SearchIcon";
import XIcon from "assets/icons/XIcon";

import styles from "./style.module.scss";
import { importCVATData } from "redux/CreateDataset/actions";

interface TaskProps {
  task: CVATTask;
  selectedID: number;
  onTaskToggle: (taskId: number) => void;
}

interface ProjectProps {
  selectedID: number;
  isExpanded: boolean;
  project: CVATProject;
  onTaskToggle: (taskId: number) => void;
  onExpandToggle: (projectId: number) => void;
  onProjectToggle: (projectId: number) => void;
}

interface CVATSelectorProps {
  cvatData: CVATProject[];
  rawCvatData: RawCvatData;
  newDatasetId: string;
  selectorRef: RefObject<HTMLDivElement>;
  onImportClick: () => void;
}

interface VirtualizedData {
  selectedID: number;
  projects: CVATProject[];
  expandedProjectIds: number[];
  onTaskToggle: (taskId: number) => void;
  onExpandToggle: (projectId: number) => void;
  onProjectToggle: (projectId: number) => void;
}

interface RowProps {
  index: number;
  style: CSSProperties;
  data: VirtualizedData;
}

interface SearchRowProps {
  index: number;
  style: CSSProperties;
  data: {
    searchTerm: string;
    selectedID: number;
    filteredData: FilteredItem[];
    onSearchItemToggle: (id: number, type: CVATTypes) => void;
  };
}

interface SearchItemProps {
  searchTerm: string;
  selectedID: number;
  singleData: FilteredItem;
  onSearchItemToggle: (id: number, type: CVATTypes) => void;
}

type SelectedCVATData = null | { id: number; type: CVATTypes };

interface RawCvatData {
  tasks: RawCVATTask[];
  projects: RawCVATProject[];
}

type FilteredItem = { type: CVATTypes; id: number; name: string };

const NoMatch = ({ searchText }: { searchText: string }) => {
  return (
    <div className={styles.noMatchBox}>
      <span className={styles.noMatchTitle}>No matches Found</span>
      <span className={styles.noMatchText}>
        {`Sorry, we couldn't find any tasks/project matching "${searchText}". Please check
          the task/project name or try using different keywords.`}
      </span>
    </div>
  );
};

const Task = ({ task, selectedID, onTaskToggle }: TaskProps) => {
  const isSelected = task.id === selectedID;

  const checkBoxClassnames = classNames(styles.checkBox, {
    [styles.selectedCheckBox]: isSelected,
  });

  return (
    <div className={styles.task}>
      <div className={checkBoxClassnames} onClick={() => onTaskToggle(task.id)}>
        {isSelected && <div className={styles.ellipse}></div>}
      </div>

      <span
        title={task.name}
        className={styles.title}
        onClick={() => onTaskToggle(task.id)}
      >
        {task.name}
      </span>

      <div className={styles.spacer}></div>
      <span className={styles.id}>ID: {task.id}</span>
    </div>
  );
};

const Project = ({
  project,
  isExpanded,
  selectedID,
  onTaskToggle,
  onExpandToggle,
  onProjectToggle,
}: ProjectProps) => {
  const isSelected = project.id === selectedID;

  const checkBoxClassnames = classNames(styles.checkBox, {
    [styles.selectedCheckBox]: isSelected,
  });

  const toggleCollapse = () => {
    onExpandToggle(project.id);
  };

  return (
    <>
      <div className={styles.project}>
        <div className={styles.dropdownIcon} onClick={toggleCollapse}>
          <ChevronIcon direction={isExpanded ? "up" : "down"} />
        </div>
        <div
          className={checkBoxClassnames}
          onClick={() => onProjectToggle(project.id)}
        >
          {isSelected && <div className={styles.ellipse}></div>}
        </div>
        <span
          title={project.name}
          className={styles.title}
          onClick={() => onProjectToggle(project.id)}
        >
          {project.name}
        </span>

        <div className={styles.spacer}></div>
        <span className={styles.id}>ID: {project.id}</span>
      </div>

      {isExpanded && (
        <div className={styles.tasks}>
          {project.tasks.map((task) => (
            <Task
              task={task}
              key={task.id}
              selectedID={selectedID}
              onTaskToggle={onTaskToggle}
            />
          ))}
        </div>
      )}
    </>
  );
};

const Row = ({ index, style, data }: RowProps) => {
  const {
    projects,
    selectedID,
    onTaskToggle,
    onExpandToggle,
    onProjectToggle,
    expandedProjectIds,
  } = data;

  const project = projects[index];

  return (
    <div style={style}>
      <Project
        project={project}
        selectedID={selectedID}
        onTaskToggle={onTaskToggle}
        onExpandToggle={onExpandToggle}
        onProjectToggle={onProjectToggle}
        isExpanded={expandedProjectIds.includes(project.id)}
      />
    </div>
  );
};

const SearchItem = ({
  searchTerm,
  singleData,
  selectedID,
  onSearchItemToggle,
}: SearchItemProps) => {
  const isSelected = selectedID === singleData.id;
  const type =
    singleData.type.charAt(0).toUpperCase() +
    singleData.type.slice(1).toLowerCase();

  const checkBoxClassnames = classNames(styles.checkBox, {
    [styles.selectedCheckBox]: isSelected,
  });

  // Function to highlight the search term
  const highlightText = (text: string, term: string) => {
    if (!term.trim()) return text;

    // Create a regular expression to match the search term
    const parts = text.split(new RegExp(`(${term})`, "gi"));

    return parts.map((part, index) =>
      part.toLowerCase() === term.toLowerCase() ? (
        <span key={index} className={styles.boldText}>
          {part}
        </span>
      ) : (
        part
      )
    );
  };

  return (
    <div className={styles.searchItem}>
      <div
        className={checkBoxClassnames}
        onClick={() => onSearchItemToggle(singleData.id, singleData.type)}
      >
        {isSelected && <div className={styles.ellipse}></div>}
      </div>
      <span
        title={singleData.name}
        className={styles.title}
        onClick={() => onSearchItemToggle(singleData.id, singleData.type)}
      >
        {highlightText(singleData.name, searchTerm)}
      </span>

      <div className={styles.typeBadge}>{type}</div>

      <div className={styles.spacer}></div>
      <span className={styles.id}>ID: {singleData.id}</span>
    </div>
  );
};

const SearchRow = ({ index, style, data }: SearchRowProps) => {
  const { searchTerm, selectedID, filteredData, onSearchItemToggle } = data;

  const singleData = filteredData[index];

  return (
    <div style={style}>
      <SearchItem
        searchTerm={searchTerm}
        singleData={singleData}
        selectedID={selectedID}
        onSearchItemToggle={onSearchItemToggle}
      />
    </div>
  );
};

const CVATSelector = ({
  cvatData,
  selectorRef,
  rawCvatData,
  newDatasetId,
  onImportClick,
}: CVATSelectorProps) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedData, setSelectedData] = useState<SelectedCVATData>(null);
  const [expandedProjectIds, setExpandedProjectIds] = useState<number[]>([]);
  const dispatch = useDispatch();

  const selectedID = selectedData ? selectedData.id : -1;
  const trimmedSearchTerm = searchTerm.toLowerCase().trim();

  const filteredData: FilteredItem[] = [
    ...rawCvatData.projects
      .filter((project) => {
        return project.name.toLowerCase().includes(trimmedSearchTerm);
      })
      .map(({ id, name }) => ({ id, name, type: CVATTypes.PROJECT as any })),
    ...rawCvatData.tasks
      .filter((task) => {
        return task.name.toLowerCase().includes(trimmedSearchTerm);
      })
      .map(({ id, name }) => ({ id, name, type: CVATTypes.TASK as any })),
  ];

  const searchResultsAvailable = filteredData.length > 0;
  const isImportButtonDisabled = !selectedData || !searchResultsAvailable;

  const importButtonClassnames = classNames(styles.importButton, {
    [styles.disabledButton]: isImportButtonDisabled,
  });

  const handleProjectToggle = (projectId: number) => {
    if (selectedData) {
      switch (selectedData.type) {
        case CVATTypes.PROJECT:
          if (selectedData.id === projectId) {
            setSelectedData(null);
          } else {
            setSelectedData({ id: projectId, type: CVATTypes.PROJECT });
          }
          break;
        case CVATTypes.TASK:
          setSelectedData({ id: projectId, type: CVATTypes.PROJECT });
          break;
      }
    } else {
      setSelectedData({ id: projectId, type: CVATTypes.PROJECT });
    }
  };

  const handleTaskToggle = (taskId: number) => {
    if (selectedData) {
      switch (selectedData.type) {
        case CVATTypes.TASK:
          if (selectedData.id === taskId) {
            setSelectedData(null);
          } else {
            setSelectedData({ id: taskId, type: CVATTypes.TASK });
          }
          break;
        case CVATTypes.PROJECT:
          setSelectedData({ id: taskId, type: CVATTypes.TASK });
          break;
      }
    } else {
      setSelectedData({ id: taskId, type: CVATTypes.TASK });
    }
  };

  const handleExpandToggle = (projectId: number) => {
    setExpandedProjectIds((prev) => {
      let result = [...prev];
      const indexOfProjectId = result.indexOf(projectId);
      if (indexOfProjectId > -1) {
        result.splice(indexOfProjectId, 1);
      } else {
        result.push(projectId);
      }
      return result;
    });
  };

  const handleSearchToggle = (id: number, type: CVATTypes) => {
    if (!selectedData) {
      setSelectedData({ id, type });
      return;
    }

    if (selectedData.id === id) {
      setSelectedData(null);
    } else {
      setSelectedData({ id, type });
    }
  };

  const handleImportClick = () => {
    if (isImportButtonDisabled) {
      return;
    } else {
      dispatch(importCVATData(newDatasetId, selectedData) as any);
      onImportClick();
    }
  };

  const getProjectItemSize = (index: number): number => {
    const project = cvatData[index];
    const isExpanded = expandedProjectIds.includes(project.id);
    const baseHeight = 40;
    const tasksHeight = isExpanded ? 40 * project.tasks.length : 0;

    return baseHeight + tasksHeight;
  };

  const calculateOptionsContainerStyle = () => {
    let totalHeight = 0;

    cvatData.forEach((project) => {
      // Base height for the project header
      totalHeight += 40;

      // Check if the project is expanded
      if (expandedProjectIds.includes(project.id)) {
        totalHeight += 40 * project.tasks.length;
      }
    });

    const maxHeight = 200;
    const finalHeight = Math.min(totalHeight, maxHeight);

    return { height: `${finalHeight}px` };
  };

  const calculateSearchItemsStyle = () => {
    if (filteredData.length <= 0) {
      //for no match found
      return { height: "211px" };
    }

    let totalHeight = 0;
    totalHeight = filteredData.length * 40;

    const maxHeight = 200;
    const finalHeight = Math.min(totalHeight, maxHeight);

    return { height: `${finalHeight}px` };
  };

  const renderList = () => {
    if (searchTerm && filteredData.length > 0) {
      return (
        <>
          <div className={styles.searchResult}>
            Search Results ({kFormatter(filteredData.length)})
          </div>

          <div
            className={styles.virtualizedSearchItems}
            style={calculateSearchItemsStyle()}
          >
            <AutoSizer>
              {({ height, width }: { height: number; width: number }) => {
                return (
                  <List
                    height={height}
                    width={width}
                    itemCount={filteredData.length}
                    itemSize={() => 40}
                    itemData={{
                      selectedID,
                      searchTerm,
                      filteredData,
                      onSearchItemToggle: handleSearchToggle,
                    }}
                  >
                    {SearchRow}
                  </List>
                );
              }}
            </AutoSizer>
          </div>
        </>
      );
    } else if (searchTerm) {
      return <NoMatch searchText={searchTerm} />;
    } else {
      return (
        <div
          className={styles.virtualizedProjects}
          style={calculateOptionsContainerStyle()}
        >
          <AutoSizer>
            {({ height, width }: { height: number; width: number }) => {
              return (
                <List
                  height={height}
                  width={width}
                  itemCount={cvatData.length}
                  itemSize={getProjectItemSize}
                  itemData={{
                    selectedID,
                    projects: cvatData,
                    onTaskToggle: handleTaskToggle,
                    onExpandToggle: handleExpandToggle,
                    onProjectToggle: handleProjectToggle,
                    expandedProjectIds: expandedProjectIds,
                  }}
                  key={JSON.stringify(expandedProjectIds)}
                >
                  {Row}
                </List>
              );
            }}
          </AutoSizer>
        </div>
      );
    }
  };

  return (
    <div className={styles.cvatSelector} ref={selectorRef}>
      <div className={styles.searchContainer}>
        <div className={styles.searchIcon}>
          <SearchIcon />
        </div>
        <div className={styles.inputBox}>
          <input
            type="search"
            value={searchTerm}
            className={styles.searchInput}
            placeholder="Search Project or Tasks..."
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          <div
            className={searchTerm ? styles.crossBtn : styles.disabledCrossBtn}
            onClick={() => setSearchTerm("")}
          >
            <XIcon />
          </div>
        </div>
      </div>

      {renderList()}

      <div className={styles.actionButtons}>
        <div className={importButtonClassnames} onClick={handleImportClick}>
          Import Selected
        </div>
      </div>
    </div>
  );
};

const mapStateToProps = (state: State) => {
  return {
    cvatData: getFormattedCVATData(state),
    rawCvatData: getRawCVATData(state),
    newDatasetId: getNewDatasetId(state),
  };
};

export default connect(mapStateToProps)(CVATSelector);
